import type {QuoteQueueItem} from '../api/quoting-api';

/**
 * Where a request stands. A person (or, later, Outlook) closes a quote: `done` or
 * `declined` come from the record's `progress`, never from the engine. While it is open, the
 * worksheet says how far along it is: `input` while the read or the worksheet left
 * questions open, `review` once MATIC closed everything it could, `sent` once an EEAA
 * workbook exists. An export does not close a quote, and a workbook on disk is all the app
 * knows, so the label says "Exported", never that the quote reached the customer.
 */
export type QuoteStatus = 'input' | 'review' | 'sent' | 'done' | 'declined';

export const STATUS_LABEL: Record<QuoteStatus, string> = {
  input: 'Needs your input',
  review: 'Ready for your review',
  sent: 'Exported',
  done: 'Done',
  declined: 'Declined',
};

export const OPEN_STATUSES: ReadonlySet<QuoteStatus> = new Set([
  'input',
  'review',
  'sent',
]);

export function quoteStatus(
  item: Pick<QuoteQueueItem, 'nQuestions' | 'chip' | 'nExports' | 'progress'>
): QuoteStatus {
  if (item.progress === 'done' || item.progress === 'declined')
    return item.progress;
  if (item.nExports > 0) return 'sent';
  if (item.nQuestions > 0 || item.chip === 'questions') return 'input';
  return 'review';
}

/**
 * The quoter's own status, the one the status dropdown sets. `done` and `declined` are the
 * record's `progress`, chosen by a person. An open quote is `in_progress` once someone has
 * worked it (an export, an ERP quote number, an owner or a waiting-on), else `to_do`: the
 * record stores only open/done/declined, so those two are read, never written.
 */
export type WorkStatus = 'to_do' | 'in_progress' | 'done' | 'declined';

export const WORK_STATUS_LABEL: Record<WorkStatus, string> = {
  // eslint-disable-next-line @typescript-eslint/naming-convention -- status keys
  to_do: 'To do',
  // eslint-disable-next-line @typescript-eslint/naming-convention -- status keys
  in_progress: 'In progress',
  done: 'Done',
  declined: 'Declined',
};

export const WORK_STATUSES: WorkStatus[] = [
  'to_do',
  'in_progress',
  'done',
  'declined',
];

export function workStatus(
  item: Pick<
    QuoteQueueItem,
    'progress' | 'nExports' | 'erpQuoteNumber' | 'assignee' | 'waitingOn'
  >
): WorkStatus {
  if (item.progress === 'done' || item.progress === 'declined')
    return item.progress;
  const started =
    item.nExports > 0 ||
    Boolean(item.erpQuoteNumber) ||
    Boolean(item.assignee) ||
    Boolean(item.waitingOn);
  return started ? 'in_progress' : 'to_do';
}

const HOUR_MS = 3_600_000;
export const DAY_MS = 24 * HOUR_MS;

/** Milliseconds since the request was received, or null when the queue gave no time. */
export function elapsedMs(
  receivedAt: string | null,
  now: number = Date.now()
): number | null {
  if (!receivedAt) return null;
  const received = new Date(receivedAt).getTime();
  return Number.isNaN(received) ? null : Math.max(0, now - received);
}

/** `3d 4h`, `5h 12m`, `40m` — the desk's elapsed-time spelling. */
export function formatDuration(ms: number): string {
  const minutes = Math.floor(ms / 60_000);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${minutes % 60}m`;
  return `${minutes}m`;
}

/** The box colour is the time, not the status: slate under a day, orange from a day on, green once closed. */
export type TimeTone = 'fresh' | 'late' | 'done';

export function timeTone(status: QuoteStatus, ms: number | null): TimeTone {
  if (!OPEN_STATUSES.has(status)) return 'done';
  return ms !== null && ms >= DAY_MS ? 'late' : 'fresh';
}

/** How loudly the Start view's waiting cards speak: 3 from four days, 2 from two, else 1. */
export function waitingLevel(ms: number | null): 1 | 2 | 3 {
  if (ms === null) return 1;
  if (ms >= 4 * DAY_MS) return 3;
  if (ms >= 2 * DAY_MS) return 2;
  return 1;
}

/** The sidebar's day headers: Today, Yesterday, a short date inside the week, Earlier beyond it. */
export function dayGroup(
  receivedAt: string | null,
  now: number = Date.now()
): string {
  if (!receivedAt) return 'Earlier';
  const received = new Date(receivedAt);
  if (Number.isNaN(received.getTime())) return 'Earlier';
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const daysAgo = Math.floor(
    (startOfToday.getTime() - received.getTime()) / DAY_MS + 1
  );
  if (daysAgo <= 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  if (daysAgo < 7)
    return received.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
    });
  return 'Earlier';
}

/** The sidebar's one-line title: customer, engine and rating when the queue knows them, else the subject. */
export function quoteTitle(
  item: Pick<QuoteQueueItem, 'customer' | 'engineKey' | 'kw' | 'subject'>
): string {
  const parts = [
    item.customer,
    item.engineKey,
    typeof item.kw === 'number' ? `${item.kw.toLocaleString()} kW` : null,
  ].filter((part): part is string => typeof part === 'string' && part !== '');
  return parts.length > 0 ? parts.join(' · ') : item.subject;
}
